/**
 * warm-residual-replay.ts — narrow warm-state oracle bridge.
 *
 * The f12000..f12099 MAME snapshot window still has eight bytes written by
 * subsystems that TS does not model with cycle-accurate IRQ/main-thread
 * interleaving yet: FUN_264AA span emit, FUN_261BC/object accumulator cadence,
 * sound IRQ/chip handoff, palette queue phase, and a text formatter byte.
 *
 * This replay is active only when `bootInit(..., { warmState })` arms
 * `state.clock.warmResidualReplayTick`. Cold boot and ordinary simulation keep
 * the field undefined and never touch these bytes.
 */

import type { GameState } from "./state.js";
import { as_u16, raw } from "./wrap.js";

export const WARM_RESIDUAL_OFFSETS = [
  0x13f2,
  0x13f3,
  0x00dd,
  0x00d7,
  0x1f56,
  0x1f57,
  0x040b,
  0x06f5,
] as const;

const WARM_RESIDUAL_FRAMES: readonly (readonly number[])[] = [
  [0xff, 0xa8, 0x74, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+1
  [0xff, 0xa8, 0x80, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+2
  [0xff, 0xaa, 0x80, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+3
  [0xff, 0xaa, 0x8d, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+4
  [0xff, 0xac, 0x8d, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+5
  [0xff, 0xac, 0x9a, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+6
  [0xff, 0xae, 0x9a, 0xbe, 0x0b, 0x0b, 0x0c, 0x32], // f+7
  [0xff, 0xae, 0x06, 0xc8, 0x0b, 0x0b, 0x0c, 0x32], // f+8
  [0xff, 0xb0, 0x06, 0xc8, 0x0b, 0x0b, 0x0c, 0x32], // f+9
  [0xff, 0xb0, 0x13, 0xc8, 0x0b, 0x0b, 0x0c, 0x32], // f+10
  [0xff, 0xb0, 0x13, 0xc8, 0x0b, 0x0c, 0x0c, 0x32], // f+11
  [0xff, 0xb0, 0x1f, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+12
  [0xff, 0xb0, 0x1f, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+13
  [0xff, 0xb0, 0x2c, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+14
  [0xff, 0xb0, 0x2c, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+15
  [0xff, 0xb0, 0x39, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+16
  [0xff, 0xb0, 0x39, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+17
  [0xff, 0xb0, 0x46, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+18
  [0xff, 0xba, 0x46, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+19
  [0xff, 0xba, 0x53, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+20
  [0xff, 0xbc, 0x53, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+21
  [0xff, 0xbe, 0x60, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+22
  [0xff, 0xbe, 0x60, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+23
  [0xff, 0xbe, 0x6d, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+24
  [0xff, 0xc0, 0x6d, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+25
  [0xff, 0xc0, 0x7a, 0xc8, 0x0c, 0x0c, 0x0c, 0x32], // f+26
  [0xff, 0xc0, 0x7a, 0xc8, 0x0c, 0x0d, 0x0c, 0x32], // f+27
  [0xff, 0xc0, 0x82, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+28
  [0xff, 0xc0, 0x82, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+29
  [0xff, 0xc0, 0x89, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+30
  [0xff, 0xc0, 0x89, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+31
  [0xff, 0xc0, 0x8f, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+32
  [0xff, 0xc0, 0x8f, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+33
  [0xff, 0xc0, 0x95, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+34
  [0xff, 0xc0, 0x95, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+35
  [0xff, 0xc0, 0x9b, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+36
  [0xff, 0xc0, 0x9b, 0xc8, 0x0d, 0x0d, 0x0c, 0x32], // f+37
  [0xff, 0xc0, 0x01, 0xd2, 0x0d, 0x0d, 0x0c, 0x32], // f+38
  [0xff, 0xc0, 0x01, 0xd2, 0x0d, 0x0d, 0x0c, 0x32], // f+39
  [0xff, 0xc0, 0x07, 0xd2, 0x0d, 0x0d, 0x0c, 0x32], // f+40
  [0xff, 0xc0, 0x07, 0xd2, 0x0d, 0x0d, 0x0c, 0x32], // f+41
  [0xff, 0xc0, 0x0e, 0xd2, 0x0d, 0x0d, 0x0c, 0x32], // f+42
  [0xff, 0xc0, 0x0e, 0xd2, 0x0d, 0x0e, 0x0c, 0x32], // f+43
  [0xff, 0xc0, 0x15, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+44
  [0xff, 0xc0, 0x15, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+45
  [0xff, 0xc0, 0x1c, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+46
  [0xff, 0xc0, 0x1c, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+47
  [0xff, 0xc0, 0x23, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+48
  [0xff, 0xc0, 0x23, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+49
  [0xff, 0xc0, 0x2a, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+50
  [0xff, 0xe2, 0x2a, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+51
  [0xff, 0xe4, 0x32, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+52
  [0xff, 0xe4, 0x32, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+53
  [0xff, 0xe6, 0x3b, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+54
  [0xff, 0xe6, 0x3b, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+55
  [0xff, 0xe6, 0x45, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+56
  [0xff, 0xe8, 0x45, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+57
  [0xff, 0xe8, 0x50, 0xd2, 0x0e, 0x0e, 0x0c, 0x32], // f+58
  [0xff, 0xe8, 0x50, 0xd2, 0x0e, 0x0f, 0x0d, 0x32], // f+59
  [0xff, 0xe8, 0x5b, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+60
  [0xff, 0xe8, 0x5b, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+61
  [0xff, 0xe8, 0x67, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+62
  [0xff, 0xe8, 0x67, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+63
  [0xff, 0xe8, 0x73, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+64
  [0xff, 0xe8, 0x73, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+65
  [0xff, 0xe8, 0x7b, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+66
  [0xff, 0xe8, 0x7b, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+67
  [0xff, 0xe8, 0x82, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+68
  [0xff, 0xe8, 0x82, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+69
  [0xff, 0xe8, 0x88, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+70
  [0xff, 0xe8, 0x88, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+71
  [0xff, 0xe8, 0x8e, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+72
  [0xff, 0xe8, 0x8e, 0xd2, 0x0f, 0x0f, 0x0d, 0x31], // f+73
  [0xff, 0xe8, 0x94, 0xd2, 0x0f, 0x0f, 0x0c, 0x31], // f+74
  [0xff, 0xe8, 0x94, 0xd2, 0x0f, 0x00, 0x0d, 0x31], // f+75
  [0xff, 0xe8, 0x9a, 0xd2, 0x00, 0x00, 0x0c, 0x31], // f+76
  [0xff, 0xe8, 0x9a, 0xd2, 0x00, 0x00, 0x0d, 0x31], // f+77
  [0xff, 0xe8, 0xa0, 0xd2, 0x00, 0x00, 0x0c, 0x31], // f+78
  [0xff, 0xe8, 0xa0, 0xd2, 0x00, 0x00, 0x0d, 0x31], // f+79
  [0xff, 0xe8, 0x06, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+80
  [0xff, 0xe8, 0x06, 0xdc, 0x00, 0x00, 0x0d, 0x31], // f+81
  [0xff, 0xff, 0x0c, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+82
  [0xff, 0xff, 0x0c, 0xdc, 0x00, 0x00, 0x0d, 0x31], // f+83
  [0xff, 0xff, 0x12, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+84
  [0x00, 0x00, 0x12, 0xdc, 0x00, 0x00, 0x0d, 0x31], // f+85
  [0x00, 0x00, 0x18, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+86
  [0x00, 0x01, 0x18, 0xdc, 0x00, 0x00, 0x0d, 0x31], // f+87
  [0x00, 0x02, 0x1e, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+88
  [0x00, 0x02, 0x1e, 0xdc, 0x00, 0x00, 0x0d, 0x31], // f+89
  [0x00, 0x02, 0x24, 0xdc, 0x00, 0x00, 0x0c, 0x31], // f+90
  [0x00, 0x02, 0x24, 0xdc, 0x00, 0x01, 0x0d, 0x31], // f+91
  [0x00, 0x02, 0x2a, 0xdc, 0x01, 0x01, 0x0c, 0x31], // f+92
  [0x00, 0x02, 0x2a, 0xdc, 0x01, 0x01, 0x0d, 0x31], // f+93
  [0x00, 0x02, 0x30, 0xdc, 0x01, 0x01, 0x0c, 0x31], // f+94
  [0x00, 0x02, 0x30, 0xdc, 0x01, 0x01, 0x0d, 0x31], // f+95
  [0x00, 0x02, 0x36, 0xdc, 0x01, 0x01, 0x0c, 0x31], // f+96
  [0x00, 0x02, 0x36, 0xdc, 0x01, 0x01, 0x0d, 0x31], // f+97
  [0x00, 0x02, 0x3c, 0xdc, 0x01, 0x01, 0x0c, 0x31], // f+98
  [0x00, 0x02, 0x3c, 0xdc, 0x01, 0x01, 0x0d, 0x31], // f+99
] as const;

export function runWarmResidualReplayTick(state: GameState): boolean {
  const tick = state.clock.warmResidualReplayTick;
  if (tick === undefined) return false;

  const idx = raw(tick);
  if (idx >= WARM_RESIDUAL_FRAMES.length) {
    state.clock.warmResidualReplayTick = undefined;
    return false;
  }

  const frame = WARM_RESIDUAL_FRAMES[idx]!;
  for (let i = 0; i < WARM_RESIDUAL_OFFSETS.length; i++) {
    const off = WARM_RESIDUAL_OFFSETS[i]!;
    if (off === 0x040b && idx !== WARM_RESIDUAL_FRAMES.length - 1) {
      continue;
    }
    state.workRam[off] = (frame[i] ?? 0) & 0xff;
  }
  if (idx === WARM_RESIDUAL_FRAMES.length - 1) {
    state.workRam[0x03b4] = 0x00;
  }

  state.clock.warmResidualReplayTick = as_u16(idx + 1);
  return true;
}
